import React, { useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { Slider } from '@mui/material';
import classNames from 'classnames';

import { useStyles } from './ExecutionDetails.styles'
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';

import { useExecutionsUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { Bar, BarChart, Customized, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { AxisZone, ZonesOverlayHorizontal } from '@shared/components/ZoneAxis';
import { StepIntervalShape } from '../StepIntervalShape';
import { StepModal } from '../StepModal';
import CustomHorizontalScroller from '../CustomScrollbar/CustomHorizontalScroller';
import { StepCompletionStatus } from 'NeoAnalytics/executionSteps';
import { SHORT_STEP_MIN_WIDTH_PX } from 'NeoAnalytics/executionSteps';
import { FormattedStep } from 'NeoAnalytics/stores/ExecutionsUI.store';

type StepEdgeSnapMarker = {
    x: number;
    y: number;
    time: number;
    label: string;
};

type VisibleExecutionRows = {
    displaySteps: FormattedStep[];
    displayAxisSteps: AxisZone[];
    hiddenDuplicateCount: number;
    hasDuplicates: boolean;
};

function timeFormatterDuration(value: number | undefined): string {
    const s = Math.max(0, Math.floor(value!));

    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    const pad2 = (n: number) => String(n).padStart(2, "0");

    return hours > 0 ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

export function getWidestString(
    strings: string[],
    fontSize: number,
    options?: {
        fontFamily?: string;
        fontWeight?: string;
        fontStyle?: string;
    }
): number | null {
    if (!strings.length) {return null;}

    const theme = useTheme<ThemeProps>();

    const fontFamily = theme.font.primary;
    const fontWeight = options?.fontWeight ?? "normal";
    const fontStyle = options?.fontStyle ?? "normal";

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {return null;}

    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

    let widestText = strings[0];
    let maxWidth = ctx.measureText(widestText).width;

    for (let i = 1; i < strings.length; i++) {
        const w = ctx.measureText(strings[i]).width;
        if (w > maxWidth) {
            maxWidth = w;
            widestText = strings[i];
        }
    }

    return maxWidth;
}

const SCROLL_HEIGHT = 40;
const AXIS_EDGE_PADDING_SECONDS = 6;
const DETAILS_AXIS_HEADER_HEIGHT = 62;
const STEP_INDEX_COLUMN_WIDTH = 42;
const TEMP_STARTED_STEP_DISPLAY_SECONDS = 5;

const STATUS_LABELS: Record<StepCompletionStatus, string> = {
    completed: 'Completed',
    failed: 'Failed',
    skipped: 'Skipped',
    aborted: 'Stopped',
    started: 'Started, not ended',
    unknown: 'Unknown',
};

const COMPLETED_FILL = '#59B8F4';
const SKIPPED_FILL = 'rgba(148, 163, 184, 0.42)';
const STARTED_FILL = 'rgba(91, 60, 196, 0.18)';

function getStatusColor(status: StepCompletionStatus, theme: ThemeProps): string {
    switch (status) {
                    case 'failed':
                        return theme.colors.red_transparent;
                    case 'skipped':
                        return SKIPPED_FILL;
                    case 'aborted':
                        return 'rgba(245, 212, 81, 0.45)';
                    case 'started':
                        return STARTED_FILL;
                    case 'completed':
                        return COMPLETED_FILL;
                    case 'unknown':
                    default:
                        return theme.colors.violet_primary;
    }
}

function getStepDuplicateKey(step: FormattedStep, axisStep?: AxisZone): string {
    return JSON.stringify({
        designedStepNumber: step.designedStepNumber ?? null,
        name: (axisStep?.label ?? step.name ?? '').trim().toLowerCase(),
        parentStepId: step.parentStepId ?? null,
    });
}

function hasStepDuplicates(steps: FormattedStep[], axisSteps: AxisZone[]): boolean {
    const seenKeys = new Set<string>();

    for (let index = steps.length - 1; index >= 0; index -= 1) {
        const duplicateKey = getStepDuplicateKey(steps[index], axisSteps[index]);

        if (seenKeys.has(duplicateKey)) {
            return true;
        }

        seenKeys.add(duplicateKey);
    }

    return false;
}

export function getVisibleExecutionRows(
    steps: FormattedStep[],
    axisSteps: AxisZone[],
    hideDuplicatedSteps: boolean,
): VisibleExecutionRows {
    if (!hideDuplicatedSteps) {

        return {
            displaySteps: steps,
            displayAxisSteps: axisSteps,
            hiddenDuplicateCount: 0,
            hasDuplicates: hasStepDuplicates(steps, axisSteps),
        };
    }

    const latestRowIndexes = new Set<number>();
    const seenKeys = new Set<string>();

    for (let index = steps.length - 1; index >= 0; index -= 1) {
        const duplicateKey = getStepDuplicateKey(steps[index], axisSteps[index]);

        if (seenKeys.has(duplicateKey)) {
            continue;
        }

        seenKeys.add(duplicateKey);
        latestRowIndexes.add(index);
    }

    const displaySteps = steps.filter((_, index) => latestRowIndexes.has(index));
    const displayAxisSteps = axisSteps.filter((_, index) => latestRowIndexes.has(index));
    const hasDuplicates = displaySteps.length !== steps.length;

    return {
        displaySteps,
        displayAxisSteps,
        hiddenDuplicateCount: steps.length - displaySteps.length,
        hasDuplicates,
    };
}

type ExecutionTimeDecorationsProps = {
    xAxisMap?: Record<string, any>;
    offset?: { top: number; left: number; width: number; height: number };
    exeDuration: number;
    theme: ThemeProps;
};

const ExecutionTimeDecorations: React.FC<ExecutionTimeDecorationsProps> = ({ xAxisMap, offset, exeDuration, theme }) => {
    if (!xAxisMap || !offset) {return null;}

    const axis = xAxisMap.time;
    const scale = axis?.scale as ((value: number) => number) | undefined;
    if (!scale) {return null;}

    const plotTop = offset.top;
    const plotBottom = offset.top + offset.height;
    const startX = Number(scale(0));
    const finishX = Number(scale(exeDuration));
    const dangerLeft = Number(scale(-AXIS_EDGE_PADDING_SECONDS));
    const dangerRight = Number(scale(exeDuration + AXIS_EDGE_PADDING_SECONDS));

    if (![startX, finishX, dangerLeft, dangerRight].every(Number.isFinite)) {
        return null;
    }

    return (
        <g pointerEvents="none">
            <defs>
                <pattern id="execution-danger-zone" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="12" height="12" fill="rgba(180, 27, 52, 0.05)" />
                    <rect width="4" height="12" fill="rgba(180, 27, 52, 0.16)" />
                </pattern>
            </defs>
            <rect x={dangerLeft} y={plotTop} width={Math.max(0, startX - dangerLeft)} height={offset.height} fill="url(#execution-danger-zone)" />
            <rect x={finishX} y={plotTop} width={Math.max(0, dangerRight - finishX)} height={offset.height} fill="url(#execution-danger-zone)" />

            <line x1={startX} x2={startX} y1={plotTop} y2={plotBottom} stroke={theme.colors.red} strokeWidth={2} strokeDasharray="3 3" />
            <line x1={finishX} x2={finishX} y1={plotTop} y2={plotBottom} stroke={theme.colors.violet_primary} strokeWidth={2} strokeDasharray="5 4" />
        </g>
    );
};

export const ExecutionDetails: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isNarrowLayout = useBreakpointDown('tablet');
    const [labelColumnWidth, setLabelColumnWidth] = useState(280);
    const [isResizing, setIsResizing] = useState(false);
    const [useDataStepNumbers, setUseDataStepNumbers] = useState(false);
    const [hideDuplicatedSteps, setHideDuplicatedSteps] = useState(true);
    const [snapMarker, setSnapMarker] = useState<StepEdgeSnapMarker | null>(null);
    const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [chartAreaWidth, setChartAreaWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const executionsUIStore = useExecutionsUIStore();
    const stepLabels = executionsUIStore.formattedExecution?.formattedAxisSteps.map(as => as.label ?? '') ?? [];
    const maxAutoLabelWidth = getWidestString(stepLabels, 16);
    // On tablet/phone the step-name column is capped much narrower (names
    // truncate with a tooltip) so the bar chart on the right stays visible.
    const preferredLabelWidth = isNarrowLayout
        ? Math.max(110, Math.min((maxAutoLabelWidth ?? 120) + 24, 170))
        : Math.max(220, Math.min((maxAutoLabelWidth ?? 180) + 72, 440));

    useEffect(() => {
        if (!isResizing) {
            return;
        }

        const previousUserSelect = document.body.style.userSelect;
        const previousCursor = document.body.style.cursor;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        const handlePointerMove = (event: MouseEvent) => {
            if (!containerRef.current) {
                return;
            }

            const bounds = containerRef.current.getBoundingClientRect();
            const nextWidth = event.clientX - bounds.left;
            const minWidth = isNarrowLayout ? 80 : 240;
            const maxAllowedWidth = Math.max(isNarrowLayout ? 140 : 260, bounds.width * 0.45);
            setLabelColumnWidth(Math.max(minWidth, Math.min(nextWidth, maxAllowedWidth)));
        };

        const handlePointerUp = () => setIsResizing(false);

        window.addEventListener('mousemove', handlePointerMove);
        window.addEventListener('mouseup', handlePointerUp);

        return () => {
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('mouseup', handlePointerUp);
            document.body.style.userSelect = previousUserSelect;
            document.body.style.cursor = previousCursor;
        };
    }, [isResizing, isNarrowLayout]);

    useEffect(() => {
        if (!isResizing) {
            setLabelColumnWidth(current => current === 280 ? preferredLabelWidth : current);
        }
    }, [preferredLabelWidth, isResizing]);

    useEffect(() => {
        const update = () => {
            if (containerRef.current) {
                setChartAreaWidth(containerRef.current.offsetWidth - labelColumnWidth - 8);
            }
        };
        update();
        const el = containerRef.current;
        if (!el) {return;}
        const obs = new ResizeObserver(update);
        obs.observe(el);

        return () => obs.disconnect();
    }, [labelColumnWidth]);

    const getStepBarHeight = (payload?: { parentStepId?: string | null; hasChildren?: boolean }) => {
        if (payload?.hasChildren) {
            return 24;
        }

        if (payload?.parentStepId) {
            return 18;
        }

        return 22;
    };

    const openStepDetails = (id: string) =>{
        if(typeof id === 'string'){
            executionsUIStore.setSelectedStepId(id);
            executionsUIStore.toggleStepModalOpen(true);
        }
    }

    const onStepModalClose = () => {
        executionsUIStore.clearSelectedStepId();
        executionsUIStore.toggleStepModalOpen(false);
    };

    return (
        <>
            <Observer>
                {() => {
                    const data = executionsUIStore.formattedExecution;
                    const exeDuration = data?.exeDuration ?? 0;
                    const totalDuration = exeDuration + AXIS_EDGE_PADDING_SECONDS * 2;
                    const basePxPerSecond = chartAreaWidth > 0 && totalDuration > 0
                        ? 10 * chartAreaWidth / totalDuration
                        : 24 * 3;
                    const pxPerSecond = basePxPerSecond * zoomLevel;
                    const formattedSteps = (data?.formattedSteps ?? []).map(step => {
                        if (!(step.isStartedOnly && step.completionStatus === 'started')) {
                            return step;
                        }

                        const [startTime] = step.interval;

                        return {
                            ...step,
                            // TODO: revert this temporary visualization once unfinished-started steps
                            // can be rendered with their dedicated state again.
                            completionStatus: 'skipped' as StepCompletionStatus,
                            interval: [startTime, startTime + TEMP_STARTED_STEP_DISPLAY_SECONDS],
                            isShort: false,
                        };
                    });
                    const displayTicks = data?.TICKS ?? [];
                    const plotWidth = ((exeDuration + AXIS_EDGE_PADDING_SECONDS * 2) / 10) * pxPerSecond;
                    const formattedAxisSteps = (data?.formattedAxisSteps ?? []).map((axisStep, index) => ({
                        ...axisStep,
                        idxLabel: useDataStepNumbers
                            ? (axisStep.idxLabel ?? String(index + 1))
                            : String(index + 1),
                    }));
                    const {
                        displaySteps,
                        displayAxisSteps,
                        hiddenDuplicateCount,
                        hasDuplicates,
                    } = getVisibleExecutionRows(formattedSteps, formattedAxisSteps, hideDuplicatedSteps);
                    const rowHeight = 40;
                    const rowsAreaHeight = Math.max(96, displaySteps.length * rowHeight);
                    const chartHeight = Math.max(160, rowsAreaHeight + SCROLL_HEIGHT + DETAILS_AXIS_HEADER_HEIGHT + 8);
                    const visibleChartHeight = `min(${chartHeight}px, calc(100vh - 230px))`;
                    const presentStatuses = Array.from(new Set(displaySteps.map(step => step.completionStatus).filter(status => status !== 'unknown')));
                    // TODO: Uncomment this once there is an option at company level to show mandatory steps.
                    // const hasMandatorySteps = Boolean(displaySteps.some(step => step.isMandatory));
                    const hasMandatorySteps = false;
                    const hasErrors = Boolean(displaySteps.some(step => step.hasErrors));
                    const hasShortSteps = Boolean(displaySteps.some(step => {
                        const intervalDuration = Math.max(0, (step.interval?.[1] ?? 0) - (step.interval?.[0] ?? 0));

                        return step.isShort || intervalDuration * pxPerSecond < SHORT_STEP_MIN_WIDTH_PX;
                    }));
                    const resolveSnapMarker = (clientX: number, clientY: number, width: number, hoveredStep?: { interval: [number, number]; name: string } | null) => {
                        if (!hoveredStep) {
                            setSnapMarker(null);

                            return;
                        }

                        const domainStart = -AXIS_EDGE_PADDING_SECONDS;
                        const domainEnd = exeDuration + AXIS_EDGE_PADDING_SECONDS;
                        const safeWidth = Math.max(1, width);
                        const thresholdPx = 12;
                        const thresholdTime = ((domainEnd - domainStart) / safeWidth) * thresholdPx;
                        const hoveredTime = domainStart + (clientX / safeWidth) * (domainEnd - domainStart);
                        const stepEdges = [
                            { time: hoveredStep.interval[0], label: `${hoveredStep.name} start` },
                            { time: hoveredStep.interval[1], label: `${hoveredStep.name} end` },
                        ];

                        const nearestEdge = stepEdges.reduce<{ time: number; label: string } | null>((nearest, edge) => {
                            if (!nearest) {
                                return edge;
                            }

                            return Math.abs(edge.time - hoveredTime) < Math.abs(nearest.time - hoveredTime) ? edge : nearest;
                        }, null);

                        if (!nearestEdge || Math.abs(nearestEdge.time - hoveredTime) > thresholdTime) {
                            setSnapMarker(null);

                            return;
                        }

                        const x = ((nearestEdge.time - domainStart) / (domainEnd - domainStart)) * safeWidth;
                        setSnapMarker({ x, y: clientY, time: nearestEdge.time, label: nearestEdge.label });
                    };

                    return (
                        <>
                            {
                                executionsUIStore.stepModalOpen &&
                                <StepModal
                                    isOpen={executionsUIStore.stepModalOpen}
                                    onRequestClose={onStepModalClose}
                                />
                            }
                            {executionsUIStore.status.isDone && displaySteps.length > 0 &&
                                <div className={styles.graphContainer} style={{ height: visibleChartHeight }}>
                                    <div className={styles.chartViewport}>
                                        <div
                                            ref={containerRef}
                                            className= {styles.chartContainer}
                                            style={{
                                                gridTemplateColumns: `${labelColumnWidth}px 8px 1fr`,
                                                minHeight: chartHeight,
                                            }}
                                        >
                                            <div className={styles.yAxisContainer}>
                                                <div className={styles.stickyStepHeader}>
                                                    <span className={styles.stickyStepHeaderIndex}>
                                                        <button
                                                            type="button"
                                                            className={styles.stepNumberToggle}
                                                            onClick={() => setUseDataStepNumbers(current => !current)}
                                                            title={useDataStepNumbers ? 'Using step numbers from data' : 'Using sequential row numbers'}
                                                        >
                                                            {useDataStepNumbers ? 'Id' : 'Nr'}
                                                        </button>
                                                    </span>
                                                    <span className={styles.stickyStepHeaderLabel}>Steps</span>
                                                    { hasDuplicates &&
                                                    <button
                                                        type="button"
                                                        className={classNames(styles.duplicateToggle, hideDuplicatedSteps && styles.duplicateToggleActive)}
                                                        onClick={() => setHideDuplicatedSteps(current => !current)}
                                                        aria-pressed={hideDuplicatedSteps}
                                                        title={hideDuplicatedSteps
                                                            ? 'Showing only the latest duplicated steps'
                                                            : 'Showing all steps, including duplicates'}
                                                    >
                                                        <span className={styles.duplicateToggleTrack}>
                                                            <span className={styles.duplicateToggleThumb} />
                                                        </span>
                                                        <span className={styles.duplicateToggleLabel}>Hide duplicates</span>
                                                        {hideDuplicatedSteps && hiddenDuplicateCount > 0 && (
                                                            <span className={styles.duplicateToggleCount}>{hiddenDuplicateCount}</span>
                                                        )}
                                                    </button>
                                                    }
                                                </div>
                                                <div className={styles.axisBody} style={{ height: rowsAreaHeight }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart
                                                            data={displaySteps}
                                                            layout="vertical"
                                                            syncId="steps-bar-sync"
                                                            margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                                                        >
                                                            <XAxis
                                                                xAxisId="time"
                                                                type="number"
                                                                padding={{ left: 0, right: 0 }}
                                                                hide
                                                            />

                                                            <YAxis
                                                                dataKey='name'
                                                                yAxisId='steps'
                                                                type='category'
                                                                strokeWidth={0.5}
                                                                tick={false}
                                                                allowDuplicatedCategory={true}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                width={labelColumnWidth}
                                                                padding={{ top: 0, bottom: 0 }}
                                                            />

                                                            <Customized component={(p: unknown) => (
                                                                <ZonesOverlayHorizontal
                                                                    {...(p as any)}
                                                                    zones={displayAxisSteps}
                                                                    yAxisId="steps"
                                                                    fixedIntervalPx={45}
                                                                    labelSide="left"
                                                                    fontSize={16}
                                                                    hideStrokes
                                                                    showIdx
                                                                    onLabelClick={(id: string) => openStepDetails(id)}
                                                                    sideLabelXPad={STEP_INDEX_COLUMN_WIDTH - 18}
                                                                    sideLabelMaxWidth={labelColumnWidth - STEP_INDEX_COLUMN_WIDTH - 18}
                                                                    minLabelWidth={labelColumnWidth - STEP_INDEX_COLUMN_WIDTH - 18}
                                                                    maxLabelWidth={labelColumnWidth - STEP_INDEX_COLUMN_WIDTH - 18}
                                                                />)}
                                                            />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <div className={styles.scrollbarSpacer} />
                                            </div>
                                            <div className={styles.resizeHandle} onMouseDown={() => setIsResizing(true)} />
                                            <div className={styles.chartArea}>
                                                <CustomHorizontalScroller plotWidth={plotWidth} theme={theme} barHeight={SCROLL_HEIGHT}>
                                                    {({ renderWidth, viewportWidth }) => (
                                                        <div className={styles.chartAreaBody} style={{ height: rowsAreaHeight + DETAILS_AXIS_HEADER_HEIGHT }}>
                                                            <div className={styles.stickyTimeHeader}>
                                                                <div
                                                                    className={styles.stickyTimeHeaderContent}
                                                                    style={{
                                                                        width: renderWidth,
                                                                        minWidth: viewportWidth,
                                                                    }}
                                                                >
                                                                    {(() => {
                                                                        const MIN_TICK_PX = 55;
                                                                        const stride = Math.max(1, Math.ceil(MIN_TICK_PX * displayTicks.length / Math.max(renderWidth, 1)));

                                                                        return displayTicks.filter((_, i) => i % stride === 0).map((tickValue: number) => {
                                                                            const left = ((tickValue + AXIS_EDGE_PADDING_SECONDS) / (exeDuration + AXIS_EDGE_PADDING_SECONDS * 2)) * renderWidth;

                                                                            return (
                                                                                <div key={`sticky-tick-${tickValue}`} className={styles.stickyTimeTick} style={{ left }}>
                                                                                    {timeFormatterDuration(tickValue)}
                                                                                </div>
                                                                            );
                                                                        });
                                                                    })()}
                                                                    <div className={styles.stickyTimeFlag} style={{ left: Math.min(renderWidth - 4, ((exeDuration + AXIS_EDGE_PADDING_SECONDS) / (exeDuration + AXIS_EDGE_PADDING_SECONDS * 2)) * renderWidth) }}>
                                                                        <div className={styles.stickyTimeFlagMain}>End</div>
                                                                        <div className={styles.stickyTimeFlagSub}>{timeFormatterDuration(exeDuration)}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className={styles.chartCanvas} style={{ height: rowsAreaHeight }}>
                                                                <div className={styles.interactiveChartArea} style={{ width: renderWidth }}>
                                                                    <ResponsiveContainer width={renderWidth} height="100%">
                                                                        <BarChart
                                                                            data={displaySteps}
                                                                            layout="vertical"
                                                                            syncId="steps-bar-sync"
                                                                            margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                                                                            onMouseMove={(chartState: any) => {
                                                                                const hoveredStep = chartState?.activePayload?.[0]?.payload as ({ stepId?: string; interval: [number, number]; name: string } | undefined);
                                                                                setHoveredStepId(hoveredStep?.stepId ?? null);

                                                                                if (!hoveredStep || typeof chartState?.chartX !== 'number') {
                                                                                    setSnapMarker(null);

                                                                                    return;
                                                                                }

                                                                                resolveSnapMarker(chartState.chartX, chartState.chartY ?? 0, renderWidth, hoveredStep);
                                                                            }}
                                                                            onMouseLeave={() => {
                                                                                setSnapMarker(null);
                                                                                setHoveredStepId(null);
                                                                            }}
                                                                        >
                                                                            <XAxis
                                                                                name="time"
                                                                                xAxisId="time"
                                                                                dataKey="time"
                                                                                type="number"
                                                                                domain={[-AXIS_EDGE_PADDING_SECONDS, exeDuration + AXIS_EDGE_PADDING_SECONDS]}
                                                                                padding={{ left: 0, right: 0 }}
                                                                                hide
                                                                            />

                                                                            <Customized component={(p: unknown) => (
                                                                                <ExecutionTimeDecorations
                                                                                    {...(p as any)}
                                                                                    exeDuration={exeDuration}
                                                                                    theme={theme}
                                                                                />)}
                                                                            />

                                                                            <Customized component={(p: unknown) => (
                                                                                <ZonesOverlayHorizontal
                                                                                    {...(p as any)}
                                                                                    zones={displayAxisSteps}
                                                                                    yAxisId="steps"
                                                                                    sideLabelXPad={25}
                                                                                    fixedIntervalPx={45}
                                                                                    labelSide="left"
                                                                                    fontSize={16}
                                                                                    hideLabels
                                                                                    showFill
                                                                                    fillOpacity={0.6}
                                                                                    oddFill={theme.colors.grey_light}
                                                                                    evenFill={theme.colors.grey_lighter}
                                                                                />)}
                                                                            />

                                                                            <YAxis
                                                                                dataKey='name'
                                                                                yAxisId='steps'
                                                                                type='category'
                                                                                strokeWidth={0.5}
                                                                                padding={{ top: 0, bottom: 0 }}
                                                                                tick={false}
                                                                                allowDuplicatedCategory={true}
                                                                                hide
                                                                                width={0}
                                                                            />

                                                                            <Bar
                                                                                dataKey="interval"
                                                                                xAxisId="time"
                                                                                yAxisId="steps"
                                                                                radius={4}
                                                                                shape={
                                                                                    <StepIntervalShape
                                                                                        barHeight={(_, payload) => getStepBarHeight(payload as { parentStepId?: string | null; hasChildren?: boolean })}
                                                                                        radius={4}
                                                                                        fill={theme.colors.violet_primary}
                                                                                        errorFill={theme.colors.red_transparent}
                                                                                        tinyFill={theme.colors.transp_violet}
                                                                                        completedFill={COMPLETED_FILL}
                                                                                        failedFill={theme.colors.red_transparent}
                                                                                        skippedFill={SKIPPED_FILL}
                                                                                        abortedFill={'rgba(245, 212, 81, 0.45)'}
                                                                                        startedFill={STARTED_FILL}
                                                                                        mandatoryStroke={theme.colors.text_heading}
                                                                                        errorIconSize={15}
                                                                                        onClick={id => openStepDetails(id)}
                                                                                        hoverFill={p => getStatusColor(p.completionStatus ?? 'completed', theme)}
                                                                                        highlightedStepId={hoveredStepId}
                                                                                    />
                                                                                }
                                                                            />
                                                                        </BarChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                                {snapMarker && (
                                                                    <div
                                                                        className={styles.snapPopup}
                                                                        style={{
                                                                            left: Math.min(Math.max(snapMarker.x + 14, 12), Math.max(renderWidth - 180, 12)),
                                                                            top: Math.min(Math.max(snapMarker.y - 54, 8), Math.max(chartHeight - SCROLL_HEIGHT - 72, 8)),
                                                                        }}
                                                                    >
                                                                        <div className={styles.snapPopupTitle}>{snapMarker.label}</div>
                                                                        <div className={styles.snapPopupTime}>{`${snapMarker.time.toFixed(2)} s`}</div>
                                                                    </div>
                                                                )}
                                                                <div className={styles.snapLine} style={{ left: snapMarker?.x ?? -9999 }} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </CustomHorizontalScroller>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.legendFooter}>
                                        {presentStatuses.map(status => (
                                            <div key={status} className={styles.legendItem}>
                                                <div className={classNames(styles.legendSwatch, status === 'started' && styles.legendStarted)} style={{ backgroundColor: status === 'started' ? 'rgba(91, 60, 196, 0.12)' : getStatusColor(status, theme) }}>
                                                    {status === 'started' && <span className={styles.legendStartedArrow} />}
                                                </div>
                                                {STATUS_LABELS[status]}
                                            </div>
                                        ))}
                                        {hasMandatorySteps &&
                                            <div className={styles.legendItem}>
                                                <div className={classNames(styles.legendSwatch, styles.legendMandatory)} style={{ backgroundColor: theme.colors.white }} />
                                                Mandatory
                                            </div>
                                        }
                                        {hasErrors &&
                                            <div className={styles.legendItem}>
                                                <div className={styles.legendSwatch} style={{ backgroundColor: theme.colors.white }}>
                                                    <span className={styles.legendErrorDot} />
                                                </div>
                                                Has errors
                                            </div>
                                        }
                                        {hasShortSteps &&
                                            <div className={styles.legendItem}>
                                                <div className={classNames(styles.legendSwatch, styles.legendShort)} style={{ backgroundColor: 'rgba(124, 58, 237, 0.12)' }} />
                                                Too Short
                                            </div>
                                        }
                                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, opacity: 0.72 }}>
                                            <div style={{ width: 160, flexShrink: 0 }}>
                                                <Slider
                                                    value={zoomLevel}
                                                    min={1}
                                                    max={5}
                                                    step={0.1}
                                                    onChange={(_, value) => setZoomLevel(value as number)}
                                                    onChangeCommitted={(_, value) => setZoomLevel([1, 2, 3, 5].reduce((n, m) => Math.abs(m - (value as number)) < Math.abs(n - (value as number)) ? m : n))}
                                                    marks={[
                                                        { value: 1 },
                                                        { value: 2 },
                                                        { value: 3 },
                                                        { value: 5 },
                                                    ]}
                                                    sx={{
                                                        color: theme.colors.violet_primary,
                                                        height: 3,
                                                        padding: '10px 0',
                                                        '& .MuiSlider-thumb': {
                                                            width: 12,
                                                            height: 12,
                                                            '&:hover': {
                                                                boxShadow: `0 0 0 5px ${theme.colors.violet_primary}22`,
                                                            },
                                                        },
                                                        '& .MuiSlider-rail': {
                                                            opacity: 0.2,
                                                        },
                                                        '& .MuiSlider-mark': {
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: theme.colors.violet_primary,
                                                            opacity: 0.35,
                                                            transform: 'translate(-50%, -50%)',
                                                        },
                                                        '& .MuiSlider-markActive': {
                                                            opacity: 0.9,
                                                            backgroundColor: '#fff',
                                                        },
                                                    }}
                                                />
                                            </div>
                                            <span style={{
                                                fontSize: 11,
                                                color: theme.colors.grey_darker,
                                                minWidth: 28,
                                                textAlign: 'right' as const,
                                                fontFamily: 'monospace',
                                                whiteSpace: 'nowrap' as const,
                                            }}>
                                                {zoomLevel.toFixed(1)}×
                                            </span>
                                            <button
                                                type="button"
                                                title="Reset zoom to 1×"
                                                onClick={() => setZoomLevel(1)}
                                                disabled={zoomLevel === 1}
                                                style={{
                                                    background: 'none',
                                                    border: `1px solid ${zoomLevel === 1 ? theme.colors.grey_darker : theme.colors.violet_primary}`,
                                                    borderRadius: 4,
                                                    width: 22,
                                                    height: 22,
                                                    padding: 0,
                                                    cursor: zoomLevel === 1 ? 'default' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 13,
                                                    color: zoomLevel === 1 ? theme.colors.grey_darker : theme.colors.violet_primary,
                                                    flexShrink: 0,
                                                    opacity: zoomLevel === 1 ? 0.3 : 0.9,
                                                    lineHeight: 1,
                                                    transition: 'opacity 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                                                }}
                                            >
                                                ↺
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                ||
                                <div className={styles.noSteps}>
                                    No Steps Detected
                                </div>
                            }
                        </>
                    );
                }}
            </Observer>
        </>
    )
}